// libs
import React, { memo, useEffect, useState, useCallback } from 'react';
import get from 'lodash/get';

// constants
import { TEXTS, SUGGESTION_TYPES } from 'shared/components/GlobalSearch/constants';
import { ROLES } from 'shared/constants';

// styles
import {
  RecentSearchContainer,
  TitleContainer,
  ClearButton,
  Term,
  Icon,
  Title,
  Container,
} from './styles';
import { CategorySwitchOverlay } from 'shared/components/GlobalSearch/components/AutoCompleteResults/styles';

// helpers
import {
  getRecentSearches,
  clearSearches,
  generateTrackingURLForSearch,
  addToRecentSearch,
  trackSearchCall,
} from 'shared/components/GlobalSearch/helpers';
import { isCrossPlatform } from 'shared/helpers/utils/url';
import {
  resolveUrlFromCatalogTag,
  getCatalogTagStoreId,
  getStoreUrl,
} from 'shared/helpers/storeUrl';

// components
import StoreBanner from 'shared/components/StoreBanner';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useStoreId } from 'shared/store/app/hooks';

// defs
import { ISuggestion } from 'shared/store/globalSearch/types';

// context
import { useKeyboardEventContext } from 'shared/components/GlobalSearch/components/KeyboardWrapper/KeyboardContext';

// components
import ElementWrapper from 'shared/components/GlobalSearch/components/KeyboardWrapper/ElementWrapper';

// hook
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';

// types
import { Theme } from '@nykaa/ui-components/themes/types';

// utility
import { hexToRgb } from '@nykaa/ui-components/styles/utils';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

interface IRecentSearchProps {
  maxRecentSearches: number;
  isVisible: boolean;
}

function RecentSearches({ maxRecentSearches, isVisible }: IRecentSearchProps) {
  const [searches, setSearches] = useState<ISuggestion[]>([]);
  const [doClearSearches, setDoClearSearches] = useState(false);
  const [activeCategorySwitch, setActiveCategorySwitch] = useState<{
    storeToRedirect: string;
    redirectUrl: string;
    productName: string;
  } | null>(null);
  const { currentIndex } = useKeyboardEventContext();
  const theme: Theme = useTheme();
  const SearchRecent = useStoreIcon(IconName.SEARCH_RECENT);
  // @ts-ignore: any since store can't be hardcoded as enums
  const storeSwitchBanner = useRemoteConfig('storeSwitchBanner');
  const currentStoreId = useStoreId();

  const handleCategorySwitch = useCallback(
    (storeToRedirect: string, redirectUrl: string, productName: string) => {
      setActiveCategorySwitch({ storeToRedirect, redirectUrl, productName });
    },
    []
  );

  const dismissCategorySwitch = useCallback(() => setActiveCategorySwitch(null), []);

  useEffect(() => {
    return () => setActiveCategorySwitch(null);
  }, []);

  useEffect(() => {
    const localSearches = getRecentSearches();
    setSearches(localSearches);
  }, []);

  // Hack to delay clearing of searches
  // as subnav checks whether click element is descendent
  // if clear eagerly the whole sub nav hides
  useEffect(() => {
    let timer: number;
    if (doClearSearches === false) {
      return;
    }

    const timerId = window.setTimeout(() => {
      clearSearches();
      setSearches([]);
    }, 200);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [doClearSearches]);

  const onClick = (searchInfo: ISuggestion) => {
    const subTitle = get(searchInfo, 'suggestionSubtitle', '');
    const { suggestionWord, type, catalog_tag } = searchInfo;
    const resolvedUrl = resolveUrlFromCatalogTag(searchInfo);
    addToRecentSearch(
      {
        suggestionWord,
        suggestionSubtitle: subTitle,
        type,
        catalog_tag,
        url: resolvedUrl,
      },
      maxRecentSearches
    );
    trackSearchCall({
      typedSearchTerm: subTitle,
      searchTerm: suggestionWord,
      searchType: type || '',
    });

    if (isCrossPlatform(resolvedUrl)) {
      window.open(resolvedUrl, '_blank');
      return;
    }

    if (type === SUGGESTION_TYPES.CATEGORY || type === SUGGESTION_TYPES.BRAND) {
      const crossStoreId = getCatalogTagStoreId(catalog_tag, currentStoreId);
      if (crossStoreId) {
        // @ts-ignore: any since store can't be hardcoded as enums
        const storeConfig = storeSwitchBanner?.[crossStoreId];
        const storeSwitch =
          (type === SUGGESTION_TYPES.BRAND && storeConfig?.brandStoreSwitch?.enabled) ||
          (type === SUGGESTION_TYPES.CATEGORY && storeConfig?.categoryStoreSwitch?.enabled);
        if (storeSwitch) {
          handleCategorySwitch(crossStoreId, resolvedUrl, suggestionWord);
          return;
        }
        window.location.href = getStoreUrl(resolvedUrl);
        return;
      }
    }

    window.location.href = generateTrackingURLForSearch({
      url: resolvedUrl,
      searchType: type,
      internalSearchTerm: suggestionWord,
      suggestionSubtitle: subTitle,
    });
  };

  if (searches.length === 0 || isVisible === false) {
    return null;
  }

  if (activeCategorySwitch) {
    const { storeToRedirect, redirectUrl, productName } = activeCategorySwitch;
    return (
      <CategorySwitchOverlay>
        <StoreBanner
          storeToRedirect={storeToRedirect}
          currentStoreProductCount={0}
          isSearch={false}
          productName={productName}
          redirectUrl={redirectUrl}
          onDismiss={dismissCategorySwitch}
        />
      </CategorySwitchOverlay>
    );
  }

  const clearAllSearches = () => {
    setDoClearSearches(true);
  };

  return (
    <RecentSearchContainer>
      <TitleContainer>
        <Title>{TEXTS.RECENT_SEARCHES}</Title>
        <ClearButton as="a" onClick={clearAllSearches} data-test="recent-search-clear">
          {TEXTS.CLEAR}
        </ClearButton>
      </TitleContainer>
      {searches.slice(0, maxRecentSearches).map((searchInfo: ISuggestion, index) => {
        const title = get(searchInfo, 'suggestionWord', '');
        const subTitle = get(searchInfo, 'suggestionSubtitle', '');
        const term = subTitle !== '' ? `${title} ${subTitle}` : title;
        const isActive = index === currentIndex;

        if (!term) {
          return null;
        }

        return (
          <ElementWrapper indexValue={index} onClick={onClick} key={term}>
            <Container
              role={ROLES.BUTTON}
              aria-label={term}
              data-test="recent-search-term"
              active={isActive}
              data-term={term}
              onClick={() => onClick(searchInfo)}
            >
              <Icon>
                <SearchRecent color={hexToRgb(theme.colors.primary, 0.4)} />
              </Icon>
              <Term>
                <span data-test="suggestion-title" data-cs-encrypt="true">
                  {title}
                </span>
                <span className="sg-guide" data-test="suggestion-subtitle" data-cs-encrypt="true">
                  {subTitle}
                </span>
              </Term>
            </Container>
          </ElementWrapper>
        );
      })}
    </RecentSearchContainer>
  );
}

export default memo(RecentSearches);
